/**
 * Vista principal del plinko: orquesta `Board`, `PourController` y
 * `BallPool` para resolver una fase de vertido completa.
 *
 * API pública (contrato del plan de implementación):
 *   `PlinkoView.mount(scene, rect)` — monta la vista en una zona de la escena.
 *   `view.start(balls, boardDef, mods)` — vierte `balls` bolas por el
 *   tablero `boardDef` (con los `BoardMod` de las cartas ya activas de la
 *   run aplicados) y devuelve una promesa que resuelve con el resultado;
 *   además emite `pour:done` por el bus de eventos para quien prefiera
 *   escuchar en vez de esperar la promesa.
 *
 * EXCEPCIÓN DELIBERADA a la regla "sin Phaser en plinko/logic": este
 * archivo es la capa de render/orquestación, así que sí importa Phaser. La
 * matemática de multiplicación, el reparto de bolas visuales y el reparto
 * de BoardMods viven en `plinko/logic/*` (puro, testeado con Vitest).
 */

import Phaser from 'phaser';
import { gameEvents } from '../core/events';
import { Rng } from '../core/rng';
import { audio } from '../core/AudioManager';
import { biomePaletteFor } from '../core/biomePalette';
import type { BoardDef, BoardMod } from '../core/types';
import { BallPool } from './BallPool';
import { BallTrail } from './BallTrail';
import { Board } from './Board';
import { applyBoardMods } from './logic/boardMods';
import type { BallContribution, BarCrossing } from './logic/barMath';
import { computePourTotal } from './logic/barMath';
import { MAX_VISUAL_BALLS } from './logic/constants';
import type { BarMeta } from './types';
import { MysteryRevealState } from './logic/mysteryReveal';
import { distributeBallWeights } from './logic/weights';
import { PourController } from './PourController';
import type { PlinkoRect, PourResult } from './types';

/** Fracción de `rect` reservada a la franja superior donde vive el vaso arrastrable. */
const CUP_ZONE_FRAC = 0.16;
/** Tiempo máximo (ms) que se espera a que un vertido termine solo, antes de forzar el cierre. */
const SAFETY_TIMEOUT_MS = 25000;

interface ActivePour {
  weights: number[];
  spawnIndex: number;
  contributions: BallContribution[];
  mysteryState: MysteryRevealState;
  resolve: (result: PourResult) => void;
  safetyTimer?: Phaser.Time.TimerEvent;
  finished: boolean;
}

export class PlinkoView {
  private scene!: Phaser.Scene;
  private boardZoneRect!: PlinkoRect;
  private ballPool!: BallPool;
  private ballTrail!: BallTrail;
  private pourController!: PourController;
  private board?: Board;
  private rng = new Rng(Date.now() >>> 0);
  private activePour?: ActivePour;
  private destroyed = false;

  /** Monta la vista dentro del rectángulo asignado (el "tablero subterráneo" de la spec). */
  mount(scene: Phaser.Scene, rect: PlinkoRect): void {
    this.scene = scene;

    const cupZoneHeight = rect.height * CUP_ZONE_FRAC;
    this.boardZoneRect = {
      x: rect.x,
      y: rect.y + cupZoneHeight,
      width: rect.width,
      height: rect.height - cupZoneHeight,
    };

    this.ballPool = new BallPool(scene, MAX_VISUAL_BALLS);
    this.ballTrail = new BallTrail(scene);
    this.pourController = new PourController({
      scene,
      restX: rect.x + rect.width / 2,
      restY: rect.y + cupZoneHeight * 0.15,
      onPourBall: (x, y, vx, vy) => this.handlePourBall(x, y, vx, vy),
    });
    this.pourController.setEnabled(false);

    scene.matter.world.on('collisionstart', this.handleCollisionStart, this);
    scene.events.on('update', this.handleUpdate, this);
    scene.events.once('shutdown', this.destroy, this);
    scene.events.once('destroy', this.destroy, this);
  }

  /**
   * Vierte `balls` bolas por `boardDef` (con `mods` de las cartas ya
   * aplicados). Resuelve con `{ ballsGained }` cuando el vertido termina;
   * también emite `pour:ready` al comenzar y `pour:done` al terminar por el
   * bus de eventos compartido. `biome` (opcional) tiñe el embudo/vaso
   * receptor del tablero con el acento del bioma del capítulo actual (ver
   * `core/biomePalette.ts`); sin bioma usa el celeste por defecto.
   */
  start(balls: number, boardDef: BoardDef, mods: BoardMod[] = [], biome?: string): Promise<PourResult> {
    if (this.activePour) {
      throw new Error('PlinkoView.start: ya hay un vertido en curso');
    }

    gameEvents.emit('pour:ready', { balls, boardId: boardDef.id });

    this.board?.destroy();
    const moddedBoard = applyBoardMods(boardDef, mods, this.rng);
    this.board = new Board(this.scene, this.boardZoneRect, moddedBoard, biome ? biomePaletteFor(biome).accent : undefined);
    this.ballPool.releaseAll();

    const weights = distributeBallWeights(balls, MAX_VISUAL_BALLS);

    return new Promise<PourResult>((resolve) => {
      const pour: ActivePour = {
        weights,
        spawnIndex: 0,
        contributions: [],
        mysteryState: new MysteryRevealState(),
        resolve,
        finished: false,
      };
      this.activePour = pour;

      if (weights.length === 0) {
        this.finishPour();
        return;
      }

      pour.safetyTimer = this.scene.time.delayedCall(SAFETY_TIMEOUT_MS, () => this.forceFinish());
      this.pourController.setEnabled(true);
    });
  }

  /** e2e/debug: fuerza el vertido completo con un arrastre sintético del vaso. */
  debugPour(): void {
    this.pourController.debugPour();
  }

  private handlePourBall(spawnX: number, spawnY: number, vx: number, vy: number): boolean {
    const pour = this.activePour;
    if (!pour || pour.finished) return false;
    if (pour.spawnIndex >= pour.weights.length) return false;
    if (!this.ballPool.canSpawn()) return true; // espera al próximo tick, no cancela el vertido

    const weight = pour.weights[pour.spawnIndex];
    if (weight === undefined) return false;
    pour.spawnIndex++;
    this.ballPool.spawn(spawnX, spawnY, weight, vx * 0.6, vy * 1.2);
    return true;
  }

  private handleUpdate(time: number): void {
    this.board?.update(time);
    if (!this.activePour) return;
    // estela sutil: un punto de rastro por bola activa, con throttle propio.
    for (const bodyId of this.ballPool.getActiveIds()) {
      const image = this.ballPool.getImage(bodyId);
      if (image) this.ballTrail.update(bodyId, image.x, image.y, time);
    }
  }

  private handleCollisionStart(event: Phaser.Physics.Matter.Events.CollisionStartEvent): void {
    if (!this.board || !this.activePour) return;
    for (const pair of event.pairs) {
      this.handlePair(pair.bodyA, pair.bodyB);
      this.handlePair(pair.bodyB, pair.bodyA);
    }
  }

  private handlePair(ballBody: MatterJS.BodyType, otherBody: MatterJS.BodyType): void {
    const board = this.board;
    if (!board || !this.ballPool.isBall(ballBody.id)) return;

    if (board.isFunnelBody(otherBody.id)) {
      this.settleBall(ballBody.id);
      return;
    }
    const meta = board.getBarMeta(otherBody.id);
    if (meta) this.crossBar(ballBody, otherBody.id, meta);
  }

  private crossBar(ballBody: MatterJS.BodyType, barBodyId: number, meta: BarMeta): void {
    const pour = this.activePour;
    const data = this.ballPool.getData(ballBody.id);
    if (!pour || !data) return;
    if (data.crossedBarIds.has(meta.id)) return; // ya cruzada por esta bola
    data.crossedBarIds.add(meta.id);

    let value = meta.value;
    if (meta.kind === 'mystery' && value === undefined) {
      value = pour.mysteryState.reveal(meta.id, this.rng);
      this.board?.revealMysteryBar(barBodyId, value);
    }

    const crossing: BarCrossing = { kind: meta.kind, value };
    data.crossings.push(crossing);

    if ((meta.kind === 'mult' || meta.kind === 'mystery') && value !== undefined) {
      audio.playMultiplier(value);
    }

    if (meta.kind === 'trampoline') {
      const image = this.ballPool.getImage(ballBody.id);
      image?.setVelocity(ballBody.velocity.x, -Math.abs(ballBody.velocity.y) - 6);
    }
  }

  private settleBall(bodyId: number): void {
    const pour = this.activePour;
    const data = this.ballPool.getData(bodyId);
    if (!pour || !data) return;
    pour.contributions.push({ weight: data.weight, crossings: [...data.crossings] });
    audio.playCoin();
    this.ballPool.release(bodyId);
    this.ballTrail.forget(bodyId);
    this.checkFinished();
  }

  private checkFinished(): void {
    const pour = this.activePour;
    if (!pour) return;
    if (pour.spawnIndex >= pour.weights.length && this.ballPool.activeCount === 0) {
      this.finishPour();
    }
  }

  /** Cierra el vertido antes de tiempo (p.ej. por el timeout de seguridad), liquidando lo que quede en curso. */
  private forceFinish(): void {
    const pour = this.activePour;
    if (!pour || pour.finished) return;
    pour.spawnIndex = pour.weights.length; // no se generan más bolas
    this.pourController.setEnabled(false);
    for (const bodyId of this.ballPool.getActiveIds()) {
      const data = this.ballPool.getData(bodyId);
      if (data) pour.contributions.push({ weight: data.weight, crossings: [...data.crossings] });
      this.ballPool.release(bodyId);
      this.ballTrail.forget(bodyId);
    }
    this.finishPour();
  }

  private finishPour(): void {
    const pour = this.activePour;
    if (!pour || pour.finished) return;
    pour.finished = true;
    pour.safetyTimer?.remove(false);
    this.pourController.setEnabled(false);

    const ballsGained = computePourTotal(pour.contributions);
    this.activePour = undefined;
    gameEvents.emit('pour:done', { ballsGained });
    pour.resolve({ ballsGained });
  }

  destroy(): void {
    // Idempotente y a prueba de teardown: durante el `shutdown` de la escena
    // el mundo de Matter puede estar ya destruido (null), y este destroy se
    // invoca dos veces (listener propio + limpieza del integrador).
    if (this.destroyed) return;
    this.destroyed = true;
    const world = this.scene?.matter?.world;
    if (world) world.off('collisionstart', this.handleCollisionStart, this);
    this.scene?.events?.off('update', this.handleUpdate, this);
    this.board?.destroy();
    this.board = undefined;
    this.ballPool?.destroy();
    this.ballTrail?.destroy();
    this.pourController?.destroy();
    this.activePour = undefined;
  }
}
